import * as Sentry from '@sentry/browser'

import { RECAPTCHA_SITE_KEY } from '../../config'

const getRecaptchaToken = () =>
  new Promise(async (resolve, reject) => {
    // fallback
    if (typeof window.grecaptcha?.execute !== 'function') {
      Sentry.addBreadcrumb({
        category: 'recaptcha',
        message: 'Not loaded, trying fallback',
        level: 'debug',
      })
      await recaptchaFallback().catch((err) => {
        Sentry.captureException(
          'Recaptcha fallback failed to load',
          err.message,
        )
        return reject(new Error('Error loading captcha'))
      })
    }
    window.grecaptcha
      .execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
      .then(resolve) // resolve token to native promise
  })

const recaptchaFallback = (timeoutMs = 2000) => {
  const timeout = new Promise((_, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id)
      reject('Timed out in ' + timeoutMs + 'ms.')
    }, timeoutMs)
  })

  const fallback = new Promise((resolve) => {
    window.recaptchaOnloadCallback = resolve
    const s = document.createElement('script')
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}&onload=recaptchaOnloadCallback`
    s.async = true
    document.body.appendChild(s)
  })

  return Promise.race([fallback, timeout])
}

const setHasEnquired = (enquiryId) => {
  const current = getHasEnquired()
  const prevEnq = current?.enquiries || []

  const data = {
    date: new Date().toISOString(),
    enquiries: [...prevEnq, enquiryId],
  }
  window.localStorage.setItem('hasEnquired', window.btoa(JSON.stringify(data)))
}

const getHasEnquired = () => {
  const data = window.localStorage.getItem('hasEnquired')
  if (!data) return null
  try {
    return JSON.parse(window.atob(data))
  } catch (err) {
    Sentry.captureException(err)
    console.log('Error decoding previous enquiry data', err.message)
    return null
  }
}

export const prewarm = (submitUrl) =>
  window
    .fetch(submitUrl, { headers: { 'X-Prewarm': 'interaction' } })
    .then((res) => {
      if (res.ok !== true)
        throw new Error(
          `Error ${res.status} prewarming lambda: '${res.statusText}'`,
        )
    })
    .catch((err) => {
      Sentry.captureException(err)
      console.log(err.message)
    })

export const handleSubmit = async ({
  formData,
  locationData = {},
  campaignData = {},
  submitUrl,
}) => {
  const recaptchaToken = await getRecaptchaToken().catch((err) => {
    //! error getting token (not error verifying request is human)
    throw new Error('Verification error')
  })

  const previousEnquiries = getHasEnquired()
  const isRepeat = previousEnquiries !== null // TODO could also be set by response

  const bodyData = {
    formData,
    recaptchaToken,
    locationData,
    campaignData,
    previousEnquiries,
  }

  try {
    const res = await window.fetch(submitUrl, {
      method: 'POST',
      body: JSON.stringify(bodyData),
      headers: { 'Content-Type': 'application/json' },
    })

    //* Check for non-200 response
    if (res.ok !== true) {
      //* Validation error
      if (res.status === 422) throw new Error('Invalid request data')

      try {
        //* Other non-200 responses may still contain useful info in body
        const body = await res.json()
        throw new Error(`${res.status} ${res.statusText}: ${body.message}`)
      } catch (e) {
        throw new Error(`${res.status} ${res.statusText}`)
      }
    }

    const resJSON = await res.json()
    // Unlikely that success not true with 200 status but check anyway
    if (resJSON.success !== true) throw new Error(resJSON.message)

    const screeningFail = resJSON.screeningFail

    trackEnquiry({ formData, isRepeat, screeningFail })
    setHasEnquired(resJSON.enquiryId)

    return { screeningFail }
  } catch (err) {
    Sentry.captureException(err)
    console.log(err.message)

    const invalid = err.message.match(/invalid/i) !== null

    // TODO track *valid* enquiries, inc. failed (assumed valid)
    if (!invalid)
      trackEnquiry({
        formData,
        isRepeat,
        submitFailed: true,
      })

    const userError = invalid ? 'Invalid submission' : 'Network error'
    throw new Error(userError)
  }
}

// TODO score enquiries (failed, screened, repeat should be low scored)
function trackEnquiry({
  formData,
  isRepeat = false,
  screeningFail = null,
  submitFailed = false,
}) {
  const payload = {
    event: 'enquiry-form-submit',
    enquiryType: formData.enquiryType,
    email: formData.submitter.email,
    tel: formData.submitter.tel.replaceAll(/[^0-9]/g, ''),
  }
  if (isRepeat) payload.isRepeat = true
  if (screeningFail === true) payload.screeningFail = true
  if (submitFailed === true) payload.failed = true

  window.dataLayer.push(payload)
}
